/**
 * admin/src/hooks/useAdmin.ts
 * - Auth state management (check session /api/auth/me + verify admin)
 * - Typed API fetch wrapper với error handling
 */

import { useState, useEffect, useCallback } from 'react';
import type { AdminUser, ApiError } from '../types/admin';

// ─── API Base URL ─────────────────────────────────────────────────────────────
// Dev: proxy qua Vite → localhost:8788 (Worker)
// Prod: gọi trực tiếp luyenthi.io.vn/api
const API_BASE = import.meta.env.DEV ? '' : 'https://luyenthi.io.vn';

// ─── Auth Hook ───────────────────────────────────────────────────────────────

interface AuthState {
  user: AdminUser | null;
  isLoading: boolean;
  isAdmin: boolean;
}

export function useAdminAuth(): AuthState & { logout: () => Promise<void> } {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAdmin: false,
  });

  useEffect(() => {
    checkSession();
  }, []);

  async function checkSession() {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
      });

      if (!res.ok) {
        setState({ user: null, isLoading: false, isAdmin: false });
        return;
      }

      const data = (await res.json()) as { user: { id: string; email: string; name: string | null; subscription_tier: string; isAdmin?: boolean } | null };

      if (!data.user) {
        setState({ user: null, isLoading: false, isAdmin: false });
        return;
      }

      // Kiểm tra admin status từ /api/admin/me (route chỉ admin mới access được)
      const adminCheck = await fetch(`${API_BASE}/api/admin/me`, {
        credentials: 'include',
      });

      const isAdmin = adminCheck.ok;

      setState({
        user: {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          subscription_tier: data.user.subscription_tier as AdminUser['subscription_tier'],
          isAdmin,
        },
        isLoading: false,
        isAdmin,
      });
    } catch {
      setState({ user: null, isLoading: false, isAdmin: false });
    }
  }

  async function logout() {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setState({ user: null, isLoading: false, isAdmin: false });
      window.location.href = '/login';
    }
  }

  return { ...state, logout };
}

// ─── Typed Fetch Wrapper ──────────────────────────────────────────────────────

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export async function adminFetch<T>(
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE}/api/admin${path}`;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  });

  if (!res.ok) {
    let errorMessage = `HTTP ${res.status}`;
    try {
      const errData = (await res.json()) as ApiError;
      errorMessage = errData.error || errorMessage;
    } catch {
      // ignore
    }
    throw new Error(errorMessage);
  }

  return res.json() as Promise<T>;
}

// ─── Generic Data Fetcher Hook ────────────────────────────────────────────────

interface UseDataState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAdminData<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): UseDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<T>(path, { params })
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(params), tick]);

  return { data, isLoading, error, refetch };
}
